import * as React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";
import Avatar from "@mui/material/Avatar";
import Badge from "@mui/material/Badge";
import Chip from "@mui/material/Chip";
import { useNavigate, useLocation } from "react-router-dom";
import { AuthContext } from "../AppContext/AuthContext";
import { CommitteeContext } from "../AppContext/CommitteeContext";
import { useContext } from "react";
import { getMenuItems, getActiveMenuPath } from "./routeConfig";

// Build menu structure from centralized config
function buildMenuList() {
  const items = getMenuItems();
  const result = [];
  let currentSection = "__top__";

  items.forEach((item) => {
    if (item.menuSection && item.menuSection !== currentSection) {
      currentSection = item.menuSection;
      result.push({ divider: true, label: item.menuSection });
    } else if (!item.menuSection && currentSection !== "__top__") {
      currentSection = "__top__";
    }
    result.push(item);
  });

  return result;
}

export const menuList = buildMenuList();

export default function AppMenu({ setOpen }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { userInfo, currentUser } = useContext(AuthContext);
  const { myRoles, isCommitteeMember } = useContext(CommitteeContext);

  const displayName = currentUser?.displayName || userInfo?.displayName || "User";
  const initials = displayName
    .split(" ")
    .map((n) => n[0])
    .join("")
    .substring(0, 2)
    .toUpperCase();

  const activeMenuPath = getActiveMenuPath(location.pathname);

  return (
    <Box sx={{ width: { xs: "min(280px, 80vw)", sm: 280 } }}>
      {/* User Header */}
      <Box
        sx={{
          p: 2.5,
          background: "linear-gradient(135deg, #1a237e 0%, #283593 100%)",
          color: "white",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Avatar
            src={currentUser?.photoURL}
            sx={{ width: 44, height: 44, border: "2px solid rgba(255,255,255,0.3)" }}
          >
            {initials}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight="bold" noWrap>
              {displayName}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.8 }} noWrap>
              {currentUser?.email}
            </Typography>
            {isCommitteeMember && myRoles.length > 0 && (
              <Chip
                label={myRoles.map(r => r.role.replace(/_/g, " ").replace(/-/g, " ")).join(", ")}
                size="small"
                sx={{
                  mt: 0.5,
                  height: 20,
                  fontSize: "0.65rem",
                  fontWeight: "bold",
                  textTransform: "capitalize",
                  bgcolor: "rgba(255,255,255,0.2)",
                  color: "white",
                  "& .MuiChip-label": { px: 1 },
                }}
              />
            )}
          </Box>
        </Box>
      </Box>

      <List sx={{ pt: 1 }}>
        {menuList.map((item, index) => {
          if (item.divider) {
            return (
              <React.Fragment key={`divider-${index}`}>
                <Divider sx={{ my: 1 }} />
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{ px: 2, py: 0.5, display: "block", fontWeight: "bold" }}
                >
                  {item.label}
                </Typography>
              </React.Fragment>
            );
          }

          const isActive = activeMenuPath === item.path;
          const Icon = item.icon;

          return (
            <ListItem key={item.path} disablePadding sx={{ px: 1 }}>
              <ListItemButton
                onClick={() => {
                  setOpen(false);
                  navigate(item.path);
                }}
                selected={isActive}
                sx={{
                  borderRadius: 2,
                  mb: 0.25,
                  "&.Mui-selected": {
                    bgcolor: "primary.50",
                    borderLeft: "3px solid",
                    borderLeftColor: "primary.main",
                    "& .MuiListItemIcon-root": { color: "primary.main" },
                    "& .MuiListItemText-primary": { fontWeight: "bold", color: "primary.main" },
                  },
                  "&:hover": { bgcolor: "action.hover" },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Icon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{ fontSize: "0.9rem" }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      {/* App version footer */}
      <Box sx={{ p: 2, mt: "auto" }}>
        <Divider sx={{ mb: 1 }} />
        <Typography variant="caption" color="text.disabled" textAlign="center" display="block">
          Community Manager v1.0
        </Typography>
      </Box>
    </Box>
  );
}
